import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { getProperties, createProperty, updateProperty, deleteProperty, getSummary } from '../lib/api';
import { toast } from 'sonner';
import { Building, Plus, Pencil, Trash2, MapPin, Layers, Calendar, FileText, Receipt, Home, BedDouble, Users, ShieldCheck, ChevronRight, Wallet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// Indian GST State Codes (matches backend GST_STATE_CODES)
const GST_STATES = [
  ["01","Jammu & Kashmir"],["02","Himachal Pradesh"],["03","Punjab"],["04","Chandigarh"],
  ["05","Uttarakhand"],["06","Haryana"],["07","Delhi"],["08","Rajasthan"],["09","Uttar Pradesh"],
  ["10","Bihar"],["11","Sikkim"],["12","Arunachal Pradesh"],["13","Nagaland"],["14","Manipur"],
  ["15","Mizoram"],["16","Tripura"],["17","Meghalaya"],["18","Assam"],["19","West Bengal"],
  ["20","Jharkhand"],["21","Odisha"],["22","Chhattisgarh"],["23","Madhya Pradesh"],
  ["24","Gujarat"],["25","Daman & Diu"],["26","Dadra & Nagar Haveli"],["27","Maharashtra"],
  ["28","Andhra Pradesh"],["29","Karnataka"],["30","Goa"],["31","Lakshadweep"],
  ["32","Kerala"],["33","Tamil Nadu"],["34","Puducherry"],["35","Andaman & Nicobar"],
  ["36","Telangana"],["37","Andhra Pradesh (New)"],["38","Ladakh"]
];

export const Properties = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [form, setForm] = useState({ 
    name: '', 
    address: '', 
    total_floors: 1, 
    system_start_date: '',
    electricity_rate_per_unit: '',
  });
  const [rateEffMonth, setRateEffMonth] = useState('');

  // Helpers for the electricity-rate "effective from" month picker
  const nextMonth = (ym) => {
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return '';
    let [y, m] = ym.split('-').map(Number);
    m += 1; if (m > 12) { m = 1; y += 1; }
    return `${y}-${String(m).padStart(2, '0')}`;
  };
  const currentMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const res = await getProperties();
      setProperties(res.data);
      // Fetch live occupancy stats per property in parallel
      const summaries = await Promise.all(
        res.data.map(p => getSummary(p.id).then(r => ({ id: p.id, data: r.data })).catch(() => ({ id: p.id, data: null })))
      );
      const statsMap = {};
      summaries.forEach(s => { if (s.data) statsMap[s.id] = s.data; });
      setStats(statsMap);
    } catch (err) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Compulsory Basic Info — block create/update if any is missing
    if (!(form.name || '').trim()) { toast.error('Property Name is required'); return; }
    if (!(form.total_floors > 0)) { toast.error('Total Floors is required'); return; }
    if (!(form.system_start_date || '').trim()) { toast.error('System Start Date is required'); return; }
    if (!(form.electricity_rate_per_unit > 0)) { toast.error('Electricity Rate (₹/unit) is required'); return; }
    try {
      // Only send Basic Info (per-property). Business details, opening balances and Guest ID
      // prefix are organisation-wide now and managed in Organisation Settings.
      const submitData = {
        name: form.name,
        address: form.address,
        total_floors: form.total_floors,
        system_start_date: form.system_start_date,
        electricity_rate_per_unit: form.electricity_rate_per_unit,
      };

      if (editingProperty) {
        // If the rate changed AND this property already has readings, send the chosen effective month
        const rateChanged = parseFloat(form.electricity_rate_per_unit) !== parseFloat(editingProperty.electricity_rate_per_unit ?? -1);
        if (rateChanged && editingProperty.latest_reading_month) {
          submitData.electricity_rate_effective_month = rateEffMonth || nextMonth(editingProperty.latest_reading_month);
        }
        await updateProperty(editingProperty.id, submitData);
        toast.success('Property updated');
      } else {
        await createProperty(submitData);
        toast.success('Property created');
      }
      setDialogOpen(false);
      setEditingProperty(null);
      setRateEffMonth('');
      setForm({
        name: '',
        address: '',
        total_floors: 1,
        system_start_date: '',
        electricity_rate_per_unit: '',
      });
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setRateEffMonth(property.latest_reading_month ? nextMonth(property.latest_reading_month) : '');
    setForm({
      name: property.name,
      address: property.address,
      total_floors: property.total_floors,
      system_start_date: property.system_start_date || '',
      electricity_rate_per_unit: property.electricity_rate_per_unit ?? '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    try {
      await deleteProperty(id);
      toast.success('Property deleted');
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingProperty(null);
    setRateEffMonth('');
    setForm({ 
      name: '', 
      address: '', 
      total_floors: 1, 
      system_start_date: '',
      electricity_rate_per_unit: '',
    });
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not Set';
    const date = String(dateStr).split('T')[0];
    const parts = date.split('-');
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    return `${day}-${month}-${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Properties</h1>
          <p className="text-slate-500 mt-1">Manage your PG properties</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {canAdd('properties') && (
            <DialogTrigger asChild>
              <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-property-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProperty ? 'Edit Property' : 'Add New Property'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4">
              <div className="w-full">
                {/* Basic Info */}
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Property Name <span className="text-rose-500">*</span></Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
                      placeholder="e.g., STAY POINT"
                      required
                      data-testid="property-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Property Address</Label>
                    <Input
                      id="address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value.toUpperCase() })}
                      placeholder="FULL ADDRESS"
                      required
                      data-testid="property-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="floors">Total Floors <span className="text-rose-500">*</span></Label>
                    <Input
                      id="floors"
                      type="number"
                      min="1"
                      value={form.total_floors}
                      onChange={(e) => setForm({ ...form, total_floors: parseInt(e.target.value) })}
                      required
                      data-testid="property-floors-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="system_start_date">System Start Date <span className="text-rose-500">*</span></Label>
                    <Input
                      id="system_start_date"
                      type="date"
                      value={form.system_start_date}
                      onChange={(e) => setForm({ ...form, system_start_date: e.target.value })}
                      required
                      data-testid="property-start-date-input"
                    />
                    <p className="text-xs text-slate-500">
                      Rent tracking starts from this date.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="electricity_rate_per_unit">Electricity Rate (₹/unit) <span className="text-rose-500">*</span></Label>
                    <Input
                      id="electricity_rate_per_unit"
                      type="number"
                      min="0"
                      step="0.5"
                      value={form.electricity_rate_per_unit}
                      onChange={(e) => setForm({ ...form, electricity_rate_per_unit: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g. 11"
                      required
                      data-testid="property-electricity-rate-input"
                    />
                    {/* Effective-from month picker: only when editing a property that already has readings and the rate has changed */}
                    {editingProperty && editingProperty.latest_reading_month &&
                      parseFloat(form.electricity_rate_per_unit) !== parseFloat(editingProperty.electricity_rate_per_unit ?? -1) ? (
                      <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <Label htmlFor="rate_eff_month" className="text-amber-900">New rate effective from (Month & Year) <span className="text-rose-500">*</span></Label>
                        <Input
                          id="rate_eff_month"
                          type="month"
                          min={nextMonth(editingProperty.latest_reading_month)}
                          value={rateEffMonth || nextMonth(editingProperty.latest_reading_month)}
                          onChange={(e) => setRateEffMonth(e.target.value)}
                          data-testid="property-rate-eff-month-input"
                        />
                        <p className="text-xs text-amber-800">
                          A reading already exists up to <b>{editingProperty.latest_reading_month}</b>. The new rate can only start from a month with <b>no reading yet</b> — past months keep their rate.
                        </p>
                      </div>
                    ) : null}
                    <p className="text-xs text-slate-500">
                      Rate per electricity unit (required). When you change it later, pick the month it takes effect from — months that already have a reading are locked, so past bills are never affected.
                    </p>
                  </div>

                  <p className="text-xs text-slate-500 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    Business Details, Financial Opening Balances and the Guest ID Prefix are now managed in <b>Organisation Settings</b> (they apply to your whole organisation).
                  </p>
                </div>

              </div>

              <div className="flex gap-3 pt-6 border-t mt-6">
                <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-blue-800 hover:bg-blue-900" data-testid="save-property-btn">
                  {editingProperty ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Properties Grid */}
      {properties.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Building className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Properties Yet</h3>
            <p className="text-slate-500 mb-4">
              {canAdd('properties') ? 'Add your first property to get started' : 'No properties available'}
            </p>
            {canAdd('properties') && (
              <Button onClick={() => setDialogOpen(true)} className="bg-blue-800 hover:bg-blue-900">
                <Plus className="h-4 w-4 mr-2" />
                Add Property
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property, idx) => {
            const s = stats[property.id];
            const occupancy = s?.occupancy_rate ?? 0;
            const hasGst = !!(property.gstin && property.gstin.length > 0);
            const invoiceReady = !!(property.business_name && property.business_name.length > 0);
            return (
            <Card
              key={property.id}
              className="dash-animate card-hover relative overflow-hidden cursor-pointer group"
              style={{ animationDelay: `${idx * 70}ms` }}
              onClick={() => navigate(`/rooms?property_id=${property.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/rooms?property_id=${property.id}`); }}
              data-testid={`property-card-${property.id}`}
            >
              {/* Accent strip */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600" />
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    {property.logo_url ? (
                      <img src={property.logo_url} alt={property.name} className="w-10 h-10 rounded-lg object-cover border border-slate-200 shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                        <Building className="h-5 w-5 text-blue-700" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <CardTitle className="text-lg truncate">{property.name}</CardTitle>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {invoiceReady && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700" data-testid={`badge-invoice-${property.id}`}>
                            <FileText className="h-2.5 w-2.5" /> Invoice-ready
                          </span>
                        )}
                        {hasGst && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700" data-testid={`badge-gst-${property.id}`}>
                            <ShieldCheck className="h-2.5 w-2.5" /> GST {property.gst_rate || 18}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {(canEdit('properties') || canDelete('properties')) && (
                    <div className="flex gap-1 shrink-0">
                      {canEdit('properties') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleEdit(property); }}
                          className="h-8 w-8"
                          data-testid={`edit-property-${property.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete('properties') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDelete(property.id); }}
                          className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`delete-property-${property.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Live occupancy stats */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Occupancy</span>
                    <span className="text-sm font-bold font-mono text-slate-900">{occupancy}%</span>
                  </div>
                  <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${occupancy >= 80 ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : occupancy >= 40 ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-rose-500 to-pink-600'}`}
                      style={{ width: `${occupancy}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                    <div data-testid={`stat-rooms-${property.id}`}>
                      <p className="text-base font-bold font-mono text-slate-900 leading-none">{s?.total_rooms ?? '—'}</p>
                      <p className="text-[10px] text-slate-500 mt-1 flex items-center justify-center gap-0.5"><Home className="h-3 w-3" />Rooms</p>
                    </div>
                    <div data-testid={`stat-beds-${property.id}`}>
                      <p className="text-base font-bold font-mono text-slate-900 leading-none">{s?.total_beds ?? '—'}</p>
                      <p className="text-[10px] text-slate-500 mt-1 flex items-center justify-center gap-0.5"><BedDouble className="h-3 w-3" />Beds</p>
                    </div>
                    <div data-testid={`stat-occupied-${property.id}`}>
                      <p className="text-base font-bold font-mono text-emerald-600 leading-none">{(s?.occupied_beds ?? 0) + (s?.notice_beds ?? 0)}</p>
                      <p className="text-[10px] text-slate-500 mt-1">Occupied</p>
                    </div>
                    <div data-testid={`stat-guests-${property.id}`}>
                      <p className="text-base font-bold font-mono text-blue-600 leading-none">{s?.total_guests ?? '—'}</p>
                      <p className="text-[10px] text-slate-500 mt-1 flex items-center justify-center gap-0.5"><Users className="h-3 w-3" />Guests</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm border-t border-slate-100 pt-3">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="truncate">{property.address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Layers className="h-4 w-4 text-slate-400 shrink-0" />
                    <span>{property.total_floors} Floor(s)</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
                    <span>Tracking from: {property.system_start_date ? formatDate(property.system_start_date) : <span className="text-slate-400 italic">Not Set</span>}</span>
                  </div>
                  {((property.opening_cash_balance || 0) > 0 || (property.opening_bank_balance || 0) > 0) && (
                    <div className="flex items-center gap-2 text-slate-600" data-testid={`opening-balance-${property.id}`}>
                      <Wallet className="h-4 w-4 text-amber-500 shrink-0" />
                      <span>Opening: <span className="font-mono font-medium">₹{(property.opening_cash_balance || 0).toLocaleString()}</span> Cash / <span className="font-mono font-medium">₹{(property.opening_bank_balance || 0).toLocaleString()}</span> Bank</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end mt-3 text-xs text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  View Rooms <ChevronRight className="h-3.5 w-3.5" />
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
